import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import EsimAPI from 'api/faber/EsimAPI';
import MenuClose from 'assets/icons/MenuClose.png';

interface ReassignPackageDialogProps {
  open: boolean;
  onClose: () => void;
  faberToken: string;
  userId: string | null;
  packageId: string | null;
}

function ReassignPackageDialog({
  open,
  onClose,
  faberToken,
  userId,
  packageId
}: ReassignPackageDialogProps) {
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const { mutate: reassignPackage, isLoading: isReassignLoading } = useMutation(
    EsimAPI.ReassignPackage,
    {
      onSuccess: () => {
        toast.success('Package reassignment successful');
        queryClient.refetchQueries({ queryKey: ['getPackageDetails'] });
      },
      onError: () => {
        toast.error('Error in reassigning package');
      },
      onSettled: () => {
        onClose();
      },
    }
  );

  const handleReassignPackage = () => {
    if (userId && email && packageId && firstName && lastName) {
      reassignPackage({ userId, email, packageId, firstName, lastName, faberToken });
    } else {
      toast.error('All fields are required');
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth={true}
      onClose={onClose}
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          fontWeight: '700',
          fontSize: '20px',
          color: '#333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Reassign Package
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            marginRight: '-8px',
            marginTop: '-8px',
          }}
        >
          <img
            style={{
              width: '20px',
              height: '20px',
            }}
            src={MenuClose}
            alt="Close"
          />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <TextField
          label="Email"
          fullWidth
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="First Name"
          fullWidth
          margin="normal"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextField
          label="Last Name"
          fullWidth
          margin="normal"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px',
          display: 'flex',
        }}
      >
        <LoadingButton variant='text' onClick={onClose}>
          Cancel
        </LoadingButton>

        <LoadingButton
          variant="contained"
          onClick={handleReassignPackage}
          loading={isReassignLoading}
          loadingIndicator={<CircularProgress size={24} />}
          sx={{
            paddingX: 4,
            fontWeight: 'bold',
          }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default ReassignPackageDialog;
